/**
 * Innstillinger in four groups (manager redesign, 2026-10-09; prototype manager-operations.js
 * settings). Every old address /innstillinger/<section> still opens: it selects its group and opens
 * that section's full editor in place, under its row. /innstillinger/<group> opens the group with
 * no section open. The summary at the top of a group reads the company's real values; a value that
 * has not loaded shows "…", one that failed shows "–", never a zero.
 */
import { useAbsenceTypes, useShiftTaskLibrary, useShiftTypes, useTeams } from '@/api/data';
import { useLabour } from '@/api/labour';
import { T } from '@/i18n';
import { breakCalcLabel, modeOf } from '@/rules/breakSetting';
import { type OpeningHours, useTenant } from '@/shell/tenant';
import { Icon } from '@/ui/icons';
import { type ReactNode, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './settings.css';

export type GroupKey = 'arbeidsmate' | 'regler' | 'vakter' | 'tilgang';
export const GROUPS: { key: GroupKey; label: string; sections: string[] }[] = [
  {
    key: 'arbeidsmate',
    label: 'Arbeidsmåte',
    sections: ['bedrift', 'modus', 'prosjekter', 'stempling', 'bemanning', 'team'],
  },
  { key: 'regler', label: 'Regler og pauser', sections: ['arbeidstid', 'pause', 'lonn'] },
  { key: 'vakter', label: 'Vakter og aktiviteter', sections: ['typer', 'oppgaver'] },
  { key: 'tilgang', label: 'Tilgang og historikk', sections: ['roller', 'varsler', 'ai'] },
];
export const SECTIONS: Record<string, { label: string; about: string }> = {
  bedrift: { label: 'Bedrift', about: 'Navn, organisasjonsnummer, tariffavtale og språk' },
  modus: {
    label: 'Arbeidsmåte og kollegavisning',
    about: 'Vaktplan, hva de ansatte ser av hverandre og godkjenning av tilgjengelighet',
  },
  prosjekter: {
    label: 'Prosjekter',
    about:
      'Prosjekter på eller av, om en vakt må ha prosjekt, og om dere sier prosjekt, oppdrag eller objekt',
  },
  stempling: { label: 'Stempling', about: 'GPS, radius og arbeidsplassen på kartet' },
  bemanning: { label: 'Bemanningsbehov', about: 'Personer som trengs per time og ukedag' },
  team: { label: 'Team', about: 'Grupperinger og filtre, aldri rettigheter' },
  arbeidstid: {
    label: 'Arbeidstid og åpningstider',
    about: 'Avtalt dag og uke, 52-ukersperioden og åpningstider',
  },
  pause: { label: 'Pause', about: 'Om pausen trekkes, og hvor lang den er' },
  lonn: {
    label: 'Lønn og satser',
    about: 'Lønnsarter, tillegg, arbeidsgiverkostnad, avspasering og Tripletex',
  },
  typer: { label: 'Vakt- og fraværstyper', about: 'Vakttyper med tider, fraværstyper med betaling og saldo' },
  oppgaver: {
    label: 'Aktivitetsbibliotek',
    about: 'Aktivitetene planleggeren tilbyr: beskrivelse, bruk, nytt navn, sammenslåing og pensjonering',
  },
  roller: { label: 'Roller og tilgang', about: 'Hva hver rolle kan se og gjøre i bedriften' },
  varsler: {
    label: 'Varsler',
    about: 'Hvilke push-varsler bedriften sender, påminnelse før vakt og tidspunkt for daglig kontroll',
  },
  ai: { label: 'AI-historikk', about: 'Samtaler og handlinger med AI-assistenten' },
};

/** The group a section or group address belongs to; an unknown address opens the first group. */
export const groupOf = (tab: string): GroupKey =>
  (GROUPS.find((g) => g.key === tab || g.sections.includes(tab)) ?? GROUPS[0])?.key as GroupKey;

const DAYS: (keyof OpeningHours)[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
function openingText(h: OpeningHours | null | undefined) {
  if (!h) return T('Ikke satt');
  const open = DAYS.map((d) => h[d]).filter((x): x is [string, string] => x != null);
  if (!open.length) return T('Ikke satt');
  const same = open.every((x) => x[0] === open[0]?.[0] && x[1] === open[0]?.[1]);
  if (!same) return T('Ulik per ukedag');
  const span = `${open[0]?.[0]}–${open[0]?.[1]}`;
  return open.length === 7 ? `${span}, ${T('alle dager')}` : `${span}, ${open.length} ${T('dager i uken')}`;
}
const num = (n: number) => n.toLocaleString('nb-NO', { maximumFractionDigits: 2 });

type Row = [label: string, value: ReactNode, section?: string | undefined];

/** The quiet label and value card at the top of a group. */
export function GroupSummary({ group }: { group: GroupKey }) {
  const { tenant, workspace, me } = useTenant();
  const labour = useLabour();
  const teamsQ = useTeams();
  const typesQ = useShiftTypes();
  const absQ = useAbsenceTypes();
  const tasksQ = useShiftTaskLibrary();
  const count = <X,>(q: { data?: X[] | undefined; isError: boolean }, f: (x: X[]) => ReactNode) =>
    q.isError ? '–' : q.data ? f(q.data) : '…';
  const maxNeed = Math.max(0, ...Object.values(labour.need).flatMap((d) => Object.values(d ?? {})));

  let rows: Row[] = [];
  if (group === 'arbeidsmate')
    rows = [
      [T('Bedrift'), tenant?.name ?? '…', 'bedrift'],
      [
        T('Registrering'),
        workspace.enabled.scheduling ? T('Vaktplan og timeregistrering') : T('Bare timeregistrering'),
        'modus',
      ],
      [T('Åpningstid'), openingText(tenant?.opening_hours), 'arbeidstid'],
      [
        T('Bemanningsbehov'),
        !labour.ready ? '…' : maxNeed > 0 ? `${T('Opptil')} ${maxNeed} ${T('samtidig')}` : T('Ikke satt'),
        'bemanning',
      ],
      [
        T('Prosjekter'),
        workspace.enabled.projects
          ? `${T('I bruk')}${tenant?.projects_required ? `, ${T('påkrevd')}` : ''}${tenant?.projects_label && tenant.projects_label !== 'Prosjekt' ? ` (${T(tenant.projects_label)})` : ''}`
          : workspace.available.projects
            ? T('Ikke i bruk')
            : T('Ikke tilgjengelig i denne utgaven'),
        // nothing to change while the module is not in this release, so no Endre (#90)
        workspace.available.projects ? 'prosjekter' : undefined,
      ],
      [
        T('Kollegavisning'),
        workspace.visibility.coworker_shifts ? T('Ser hverandres publiserte vakter') : T('Bare egne vakter'),
        'modus',
      ],
      // issue #70: the switch is in Arbeidsmåte og kollegavisning
      [
        T('Tilgjengelighet'),
        tenant?.availability_requires_approval
          ? T('Må godkjennes av en leder')
          : T('Meldes uten godkjenning'),
        'modus',
      ],
      [
        T('GPS på stempling'),
        tenant?.is_gps_enabled ? `${T('På')}, ${tenant.gps_radius_meters ?? '–'} m` : T('Av'),
        'stempling',
      ],
      [T('Team'), count(teamsQ, (x) => x.filter((t) => t.name).length), 'team'],
    ];
  if (group === 'regler') {
    const week = tenant?.agreed_week_hours;
    rows = [
      [
        T('Avtalt daglig arbeidstid'),
        tenant?.shift_length_hours != null ? `${num(Number(tenant.shift_length_hours))} t` : T('Ikke satt'),
        'arbeidstid',
      ],
      [
        T('Avtalt ukentlig arbeidstid'),
        week != null && week !== '' ? `${num(Number(week))} t` : T('Ikke satt, 37,5 t brukes'),
        'arbeidstid',
      ],
      [T('Tariffavtale'), tenant?.tariff_bound ? T('Ja') : T('Nei'), 'bedrift'],
      [T('Pauseberegning'), tenant ? breakCalcLabel(modeOf(tenant)) : '…', 'pause'],
      [
        T('Fast pauselengde'),
        tenant?.lunch_length_minutes != null ? `${tenant.lunch_length_minutes} min` : T('Ikke satt'),
        'pause',
      ],
      [T('Lønnsart for vanlige timer'), tenant?.regular_pay_code || T('Ikke satt'), 'lonn'],
      [T('Lønnsart for tillegg 1. og 17. mai'), tenant?.hoytid_pay_code || T('Ikke satt'), 'lonn'],
      [
        T('Ingen tillegg for fastlønte unntatt arbeidstidsreglene'),
        tenant?.exempt_salaried_no_supplements ? T('Ja') : T('Nei'),
        'lonn',
      ],
      [
        T('Merarbeid for deltidsansatte på fastlønn'),
        tenant?.merarbeid_plain_pay !== false ? T('Ordinær lønn') : T('Overtid'),
        'lonn',
      ],
    ];
  }
  if (group === 'vakter')
    rows = [
      [T('Vakttyper'), count(typesQ, (x) => x.length), 'typer'],
      [
        T('Fraværstyper'),
        count(absQ, (x) => `${x.filter((a) => a.is_active !== false).length} ${T('i bruk av')} ${x.length}`),
        'typer',
      ],
      [
        T('Aktiviteter'),
        count(tasksQ, (x) => `${x.filter((a) => a.is_active).length} ${T('i bruk av')} ${x.length}`),
        'oppgaver',
      ],
    ];
  if (group === 'tilgang')
    rows = [[T('Din rolle'), me?.is_admin ? T('Administrator') : (me?.role ?? '…'), 'roller']];

  const label = GROUPS.find((g) => g.key === group)?.label ?? '';
  return (
    <section className="mcard stsum" aria-labelledby="stsum-h">
      <div className="mch">
        <h2 id="stsum-h">{T(label)}</h2>
      </div>
      <dl data-testid="settings-summary">
        {rows.map(([k, v, s]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>
              {v}
              {s && (
                <Link
                  className="mlink"
                  to={`/innstillinger/${s}`}
                  aria-label={`${T('Endre')} ${k.toLowerCase()}`}
                >
                  <Icon name="sliders" />
                  {T('Endre')}
                </Link>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The group's sections as rows; the one in the address is open under its row with its full
 * editor. A row is a link, so Back closes it and the address can be shared.
 */
export function SectionList({
  group,
  open,
  hide,
  children,
}: {
  group: GroupKey;
  open: string | null;
  hide?: string[];
  children: ReactNode;
}) {
  const g = GROUPS.find((x) => x.key === group);
  const ref = useRef<HTMLElement>(null);
  // a section opened from another page (Dagen → Bemanning) or a row starts at its heading
  useEffect(() => {
    if (open) ref.current?.scrollIntoView?.({ block: 'start' });
  }, [open]);
  if (!g) return null;
  return (
    <section className="mcard stlist">
      <div className="mch">
        <h2>
          {T('Innstillinger i')} {T(g.label).toLowerCase()}
        </h2>
      </div>
      <ul>
        {g.sections
          .filter((s) => !hide?.includes(s))
          .map((s) => {
            const meta = SECTIONS[s];
            const on = s === open;
            return (
              <li key={s} className={on ? 'on' : undefined}>
                <Link
                  to={`/innstillinger/${on ? group : s}`}
                  className="strow"
                  aria-expanded={on}
                  aria-current={on ? 'page' : undefined}
                  data-testid={`section-${s}`}
                >
                  <span>
                    <b>{T(meta?.label ?? s)}</b>
                    <small>{T(meta?.about ?? '')}</small>
                  </span>
                  <span className="stchev" aria-hidden="true">
                    <Icon name="chevronRight" />
                  </span>
                </Link>
                {on && (
                  <section className="stbody" ref={ref} aria-label={T(meta?.label ?? s)}>
                    {children}
                  </section>
                )}
              </li>
            );
          })}
      </ul>
    </section>
  );
}
